import { useContext, useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import { loadEmailAlerts } from 'components/Dashboard/utilities/loadKeyWords';
import usePushNotifications from 'components/Settings/EmailAlerts/usePushNotifications';
import { isServiceWorkerSupported } from 'components/Settings/EmailAlerts/pushServiceWorkerUtils';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import LocalStorage from 'utils/LocalStorage';
import useStealthModeConditional from 'components/useStealthModeConditional';

const usePushNotificationsValidations = () => {
  const [showPushNotificationsModal, setShowPushNotificationsModal] = useState(false);

  const { isAuthenticated } = useAuth0();
  const { getAccessToken } = useGetAccessToken();
  const { newAgencyPlan } = useTopicOrClientWord();
  const { generalApiCall } = useGeneralApiCall();
  const { subscribePushNotifications } = usePushNotifications();
  const { validStealthModeFeatureUsers } = useStealthModeConditional();

  const location = useLocation();
  const logoutPage = location.pathname === '/logout';

  const pushNotificationsValidations = async () => {
    const pathnamesToExclude = ['/login', '/register', '/login-success', '/initialise', '/accept-invite'];
    if (!logoutPage && isAuthenticated && !pathnamesToExclude?.includes(location?.pathname)) {
      try {
        if (
          !isServiceWorkerSupported() ||
          !('PushManager' in window) ||
          !('Notification' in window) ||
          !validStealthModeFeatureUsers
        )
          return;
        let alertSettings;
        if (newAgencyPlan) {
          const results = await generalApiCall({
            pathname: '/api/alert-settings/all-teams',
            method: 'get',
            needsAuthentication: true,
            notShowErrorMessage: true,
          });
          alertSettings = results?.flatMap((item) => item.alertSettings) ?? [];
        } else {
          alertSettings = (await loadEmailAlerts(getAccessToken)) ?? [];
        }

        const hasPushNotificationAlert = alertSettings.some((alert) =>
          alert.alertTypes?.split(', ').includes('PushNotification')
        );
        if (!hasPushNotificationAlert) return;

        if (Notification.permission === 'granted') {
          await subscribePushNotifications();
        } else if (!LocalStorage.getItem('notShowPushNotificationModalAgain')) {
          setShowPushNotificationsModal(true);
        }
      } catch (error) {
        console.error('registerPushServiceWorker error:', error);
      }
    }
  };

  return {
    pushNotificationsValidations,
    pushNotificationModalState: { showPushNotificationsModal, setShowPushNotificationsModal },
  };
};

export default usePushNotificationsValidations;
